import { useEffect, useState } from 'react'
import type { ContentDetail } from '@cms/contracts'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { setContentField } from '../api/endpoints.js'
import { parseVersionConflict } from '../domain/conflict.js'
import { absoluteDateTime, relativeTime } from '../domain/format.js'
import { resolveReason } from '../domain/reason.js'
import { editorialDateOf, editorialDateValue, fromLocalInputValue, scheduleState, toLocalInputValue } from '../domain/schedule.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { useSnackbar } from '../ui/overlays.js'
import { TextField } from '../ui/fields.js'
import { Button, Card, LinkButton, StatusChip } from '../ui/primitives.js'

/**
 * "Programmazione" in the editor's side panel (document/61, E1): the
 * editorial date of the content, written with the command that already
 * exists — `PATCH /contents/{id}/field` on `publishedAt` (or `PublishDate`
 * for imported documents). The gateway moves the coordinator's alarm by
 * itself when that path changes; a published content with a future date
 * stays off the site until then.
 */
export function ScheduleCard({ content, siteId, readOnly, blocked, note, onSaved }: {
  content: ContentDetail
  siteId: string
  readOnly: boolean
  /** Other changes are waiting in the form: this card writes on its own and would race them. */
  blocked: boolean
  /** The editor's "Nota per lo storico", when the person wrote one. */
  note: string
  onSaved: () => Promise<unknown>
}) {
  const { t, locale } = useI18n()
  const snackbar = useSnackbar()
  const current = editorialDateOf(content.data)
  const [input, setInput] = useState(() => toLocalInputValue(current.value))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A reload with a different date (someone else, or this card) resets the field.
  useEffect(() => setInput(toLocalInputValue(current.value)), [current.value])

  const chosen = fromLocalInputValue(input)
  const now = Date.now()
  const state = scheduleState(content.status, current.value, now)
  const changed = chosen !== current.value && !(chosen === null && input.trim() === '' && current.value === null)

  const write = async (value: number | null) => {
    setBusy(true)
    setError(null)
    const reason = value === null
      ? resolveReason(note, t('schedule.reason.clear'))
      : resolveReason(note, t('schedule.reason.set', { date: absoluteDateTime(value, locale) }))
    try {
      await setContentField(content.id, {
        path: current.key,
        value: value === null ? null : editorialDateValue(value, current.raw),
        reason,
        expectedVersion: content.version,
      }, newIdempotencyKey())
      snackbar(value === null ? t('schedule.card.cleared') : t('schedule.card.saved'))
      await onSaved()
    } catch (err) {
      // On a 409 the date typed here stays; the content reloads under it and a second press retries.
      if (parseVersionConflict(err)) {
        setError(t('schedule.card.conflict'))
        await onSaved()
      } else setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally {
      setBusy(false)
    }
  }

  return <Card title={t('schedule.card.title')} icon="event">
    <div className="form-stack">
      {state === 'scheduled' && current.value !== null && <StatusChip tone="info" icon="schedule">{t('schedule.card.scheduled', { when: relativeTime(current.value, now, locale) })}</StatusChip>}
      {state === 'waitingForPublish' && <StatusChip tone="warning" icon="warning">{t('schedule.card.waiting')}</StatusChip>}
      {state === 'past' && current.value !== null && <p className="field-support">{t('schedule.card.past', { when: absoluteDateTime(current.value, locale) })}</p>}
      {state === 'none' && <p className="field-support">{t('schedule.card.none')}</p>}
      {state === 'waitingForPublish' && <p className="field-support">{t('schedule.card.waitingHelp')}</p>}
      <TextField
        label={t('schedule.card.date')}
        type="datetime-local"
        value={input}
        disabled={readOnly || busy}
        onChange={(event) => setInput(event.target.value)}
        error={error}
        supporting={blocked && !readOnly ? t('schedule.card.blocked') : t('schedule.card.help')}
      />
      {!readOnly && <div className="card-footer-actions">
        {current.value !== null && <Button variant="text" icon="close" disabled={busy || blocked} onClick={() => void write(null)}>{t('schedule.card.clear')}</Button>}
        <Button variant="tonal" icon="check" disabled={busy || blocked || !changed || chosen === null} onClick={() => chosen !== null && void write(chosen)}>
          {busy ? t('common.saving') : t('schedule.card.save')}
        </Button>
      </div>}
      <LinkButton variant="text" small icon="calendar_month" to={paths.schedule(siteId)}>{t('schedule.card.openCalendar')}</LinkButton>
    </div>
  </Card>
}
